import { jsx as _jsx } from "react/jsx-runtime";
import { getFilms, isEmptyArray, isFilmAvailableForBookings, loadFilmAvailabilities, loadFilms, logger } from '@vista-digital/occ-domain';
import { observer } from 'mobx-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../../context/occ/hooks/use-i18n';
import { DefaultDropdownOptionTemplate } from '../../../subcomponents/form/input/dropdown/option-templates/default';
import { FilmDropdownOptionTemplate, getDropdownOptionsForFilms } from '../../../subcomponents/form/input/dropdown/option-templates/film';
import { QuickNavigatorContextInstance } from '../types/context';
import { DropdownType, QuickNavigatorDropdown } from './dropdown';
export const QuickNavigatorFilmDropdown = observer(() => {
    const i18n = useI18n();
    const { displayText, config, cdnOptions, state, assets } = useContext(QuickNavigatorContextInstance);
    const { enableSearch, mediaType } = config.films;
    const selectedSiteId = state.dropdownSelections.site;
    const [messageType, setMessageType] = useState();
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        (async () => {
            setIsLoading(true);
            try {
                const siteIds = selectedSiteId ? [selectedSiteId] : undefined;
                await Promise.all([loadFilms(siteIds), loadFilmAvailabilities(selectedSiteId)]);
                setMessageType(undefined);
            }
            catch (error) {
                logger.error(error);
                setMessageType(QuickNavigatorFilmDropdownMessageType.FailedToLoadFilms);
            }
            finally {
                setIsLoading(false);
            }
        })();
    }, [selectedSiteId]);
    const films = useMemo(() => {
        var _a, _b;
        if (isLoading)
            return [];
        const siteIds = selectedSiteId ? [selectedSiteId] : undefined;
        return (_b = (_a = getFilms(siteIds)) === null || _a === void 0 ? void 0 : _a.filter(film => isFilmAvailableForBookings(film.id, selectedSiteId))) !== null && _b !== void 0 ? _b : [];
    }, [isLoading, selectedSiteId]);
    useEffect(() => {
        if (isEmptyArray(films) && !isLoading && !messageType) {
            if (selectedSiteId) {
                setMessageType(QuickNavigatorFilmDropdownMessageType.NoFilmsForSite);
            }
            else {
                setMessageType(QuickNavigatorFilmDropdownMessageType.NoFilms);
            }
        }
    }, [films, isLoading, messageType, selectedSiteId]);
    const dropdownOptions = getDropdownOptionsForFilms(films, selectedSiteId, {
        filmThumbnailMediaType: mediaType,
        filmThumbnailCdnOptions: cdnOptions.filmThumbnailImage,
        censorRatingCdnOptions: cdnOptions.censorRatingIcon
    }, i18n);
    let messageProps = getQuickNavigatorFilmDropdownMessageProps(messageType, assets, displayText);
    messageProps = state.isModalError? { ...messageProps, errorMessageHelpText: 'Please select a film' }: messageProps;
    return (_jsx(QuickNavigatorDropdown, Object.assign({}, messageProps, { type: DropdownType.Film, options: dropdownOptions, modal: Object.assign(Object.assign({}, messageProps === null || messageProps === void 0 ? void 0 : messageProps.modal), { displayAsModalOnMobile: config.films.displayAsModalOnMobile, heading: displayText.filmModalHeading, searchInputPlaceholder: displayText.filmModalSearchInputPlaceholder, noSearchResultsFoundMessage: displayText.noFilmSearchResultsFoundModalMessage, noSearchResultsFoundIconName: assets.noFilmsFoundModalIconName }), loading: isLoading, enableSearch: enableSearch, placeholder: displayText.filmDropdownPlaceholder, label: displayText.filmDropdownLabel, optionTemplate: FilmDropdownOptionTemplate, optionPreviewTemplate: DefaultDropdownOptionTemplate, noSearchResultsFoundMessage: displayText.noFilmsFoundErrorMessage })));
});
QuickNavigatorFilmDropdown.displayName = 'QuickNavigatorFilmsDropdown';
var QuickNavigatorFilmDropdownMessageType;
(function (QuickNavigatorFilmDropdownMessageType) {
    QuickNavigatorFilmDropdownMessageType["FailedToLoadFilms"] = "FailedToLoadFilms";
    QuickNavigatorFilmDropdownMessageType["NoFilms"] = "NoFilms";
    QuickNavigatorFilmDropdownMessageType["NoFilmsForSite"] = "NoFilmsForSite";
})(QuickNavigatorFilmDropdownMessageType || (QuickNavigatorFilmDropdownMessageType = {}));
const getQuickNavigatorFilmDropdownMessageProps = (messageType, assets, displayText) => {
    switch (messageType) {
        case QuickNavigatorFilmDropdownMessageType.FailedToLoadFilms:
            return {
                errorMessageHelpText: displayText.failedToLoadFilmsErrorMessage,
                modal: {
                    errorTitle: displayText.failedToLoadFilmsModalErrorTitle,
                    errorMessage: displayText.failedToLoadFilmsModalErrorMessage,
                    errorIconName: assets.failedToLoadFilmsModalErrorIconName
                }
            };
        case QuickNavigatorFilmDropdownMessageType.NoFilms:
            return {
                modal: {
                    errorTitle: displayText.noFilmsModalErrorTitle,
                    errorMessage: displayText.noFilmsModalErrorMessage,
                    errorIconName: assets.noFilmsModalErrorIconName
                }
            };
        case QuickNavigatorFilmDropdownMessageType.NoFilmsForSite:
            return {
                modal: {
                    errorTitle: displayText.noFilmsForSiteModalErrorTitle,
                    errorMessage: displayText.noFilmsForSiteModalErrorMessage,
                    errorIconName: assets.noFilmsForSiteModalErrorIconName
                }
            };
        default:
            return undefined;
    }
};
//# sourceMappingURL=film-dropdown.js.map